// apps/spa/src/components/settings/RoleToggleManager.tsx
// SPA UI for managing the guild_toggleable_roles list — which roles members can
// toggle on/off with the /role-toggle Discord slash command.
//
// Table columns: Label · Role ID · Position · Actions (Delete with AlertDialog)
// Form: label + role_id (snowflake) + optional position → POST /api/toggleable-roles
//
// Source: 07-03-PLAN.md Task 3 spec. Follows CommandTable.tsx + shadcn primitives.
// Does NOT introduce new shadcn components beyond base-nova preset.

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { createToggleableRoleSchema, type CreateToggleableRoleBody } from '@guardian/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  useToggleableRoles,
  useCreateToggleableRole,
  useDeleteToggleableRole,
} from '@/hooks/useToggleableRoles';

export function RoleToggleManager() {
  const { data, isLoading } = useToggleableRoles();
  const create = useCreateToggleableRole();
  const remove = useDeleteToggleableRole();
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const form = useForm<CreateToggleableRoleBody>({
    resolver: zodResolver(createToggleableRoleSchema),
    defaultValues: { roleId: '', label: '', position: undefined },
  });

  const { formState: { errors } } = form;

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading roles…</div>;
  }

  return (
    <section className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary/80">Settings</p>
        <h1 className="font-display text-3xl">Self-service roles</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Roles members can toggle with the <code>/role-toggle</code> command.
        </p>
      </header>

      {/* Add role form */}
      <form
        className="grid gap-3 sm:grid-cols-[2fr_2fr_1fr_auto] items-end"
        onSubmit={form.handleSubmit(async (vals) => {
          try {
            await create.mutateAsync(vals);
            toast.success(`Added "${vals.label}"`);
            form.reset();
          } catch {
            toast.error('Failed to add role');
          }
        })}
      >
        <div>
          <Label htmlFor="label">Label</Label>
          <Input
            id="label"
            {...form.register('label')}
            placeholder="Raider"
            aria-invalid={!!errors.label}
          />
          {errors.label && (
            <p className="text-xs text-destructive mt-1">{errors.label.message}</p>
          )}
        </div>
        <div>
          <Label htmlFor="roleId">Role ID</Label>
          <Input
            id="roleId"
            {...form.register('roleId')}
            placeholder="123456789012345678"
            aria-invalid={!!errors.roleId}
          />
          {errors.roleId && (
            <p className="text-xs text-destructive mt-1">{errors.roleId.message}</p>
          )}
        </div>
        <div>
          <Label htmlFor="position">Position</Label>
          <Input
            id="position"
            type="number"
            {...form.register('position', {
              setValueAs: (v) => (v === '' || v === undefined ? undefined : Number(v)),
            })}
            placeholder="0"
          />
        </div>
        <Button type="submit" disabled={create.isPending}>
          Add
        </Button>
      </form>

      {/* Role table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label</TableHead>
            <TableHead>Role ID</TableHead>
            <TableHead>Position</TableHead>
            <TableHead aria-label="Actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {(data ?? []).map((row) => (
            <TableRow key={row.roleId}>
              <TableCell>{row.label}</TableCell>
              <TableCell className="font-mono text-xs">{row.roleId}</TableCell>
              <TableCell>{row.position ?? '—'}</TableCell>
              <TableCell className="text-right">
                <AlertDialog
                  open={pendingDelete === row.roleId}
                  onOpenChange={(open) => !open && setPendingDelete(null)}
                >
                  <AlertDialogTrigger
                    className="inline-flex h-8 items-center justify-center rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    onClick={() => setPendingDelete(row.roleId)}
                  >
                    Delete
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Remove &ldquo;{row.label}&rdquo; from /role-toggle?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Members with the role keep it; this only removes it from the
                        autocomplete list.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={async () => {
                          try {
                            await remove.mutateAsync(row.roleId);
                            toast.success('Removed');
                          } catch {
                            toast.error('Delete failed');
                          } finally {
                            setPendingDelete(null);
                          }
                        }}
                      >
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}
